import * as _ from "lodash";
import { isNullOrUndefined } from "util";

import { TradeRecommendation } from "@models/trading/tradeRecommendation";
import { AssetClassNames } from "@common/enums/AssetClassType";
import { TradingRepo } from "@services/trading.repo";
import { TradeRestriction } from "@models/trading/tradeRestriction";

class PortfolioSimulationController implements angular.IOnChanges {
  public tradeSheetOnly: boolean = false;
  public portfolio: { portfolioId: number; clientId: number };
  public tradeRecommendations: TradeRecommendation[];
  private cashBalances: any[];

  public isLoading: boolean = false;

  public totalBuy: number = 0;
  public totalSell: number = 0;

  public assetClassesTotal: number;
  public targetAssetClassesTotal: number;

  private targetAssetClass: { name: string; total: number }[];
  private modelChange: boolean = false;

  constructor(
    private $rootScope: angular.IRootScopeService,
    private $mdDialog: angular.material.IDialogService,
    private tradingRepo: TradingRepo,
    private notifyjs: any
  ) {}

  $onChanges(onChangesObj: angular.IOnChangesObject): void {
    if (onChangesObj.tradeRecommendations || onChangesObj.cashBalances) {
      this.updateAssetClasses();
    }
  }

  public getTotalCash() {
    if (!this.targetAssetClass) {
      return 0;
    }
    const assetClass = this.targetAssetClass.find(
      (assetClass) => assetClass.name == AssetClassNames.CASH
    );
    if (!assetClass) {
      return 0;
    }
    return assetClass.total;
  }

  public showActiveTradeDetails(securityId: number) {
    this.tradingRepo
      .getActiveTradeDetails(securityId, this.portfolio.portfolioId)
      .then((resp) => {
        const tempScope = this.$rootScope.$new(true) as any;
        tempScope.tradeDetails = resp.data;

        return this.$mdDialog.show({
          template:
            '<trade-details-modal trade-details="tradeDetails"></trade-details-modal>',
          scope: tempScope,
        });
      });
  }

  public showRestrictionDetail(tradeRestriction: TradeRestriction) {
    if (!tradeRestriction) {
      return;
    }

    return `Active: ${tradeRestriction.enabled ? "Yes" : "No"}
Restriction: ${tradeRestriction.restriction()}
Type: ${tradeRestriction.type()}
Created By: ${
      tradeRestriction.createdBy ? tradeRestriction.createdBy.name : "Unknown"
    }
Date Created: ${new Date(tradeRestriction.dateCreated)}`;
  }

  public getAssetClassTotal(assetClassTotals, assetClassName: string) {
    const assetClassTotal = assetClassTotals.find(
      (assetClass) => assetClass.name == assetClassName
    );
    return isNullOrUndefined(assetClassTotal) ? 0 : assetClassTotal.total;
  }

  public updateAssetClasses() {
    this.totalBuy = 0;
    this.totalSell = 0;

    const netBuySell = this.tradeRecommendations.reduce((total, tr) => {
      const diff = tr.buySellQuantity;
      if (diff === 0) {
        return total;
      }

      const val = tr.price * diff;
      if (diff < 0) {
        this.totalSell += val;
      } else if (diff > 0) {
        this.totalBuy += val;
      }
      return val + total;
    }, 0);

    this.targetAssetClass = [
      {
        name: AssetClassNames.EQUITY,
        total: this.tradeRecommendations
          .filter(
            (tr) => tr.security.assetClass.name === AssetClassNames.EQUITY
          )
          .reduce((total, tr) => total + tr.targetMarketValue, 0),
      },
      {
        name: AssetClassNames.FIXED_INCOME,
        total: this.tradeRecommendations
          .filter(
            (tr) => tr.security.assetClass.name === AssetClassNames.FIXED_INCOME
          )
          .reduce((total, tr) => total + tr.marketValueDisplayCurrency, 0),
      },
      {
        name: AssetClassNames.PRECIOUS_METALS,
        total: this.tradeRecommendations
          .filter(
            (tr) =>
              tr.security.assetClass.name === AssetClassNames.PRECIOUS_METALS
          )
          .reduce((total, tr) => total + tr.marketValueDisplayCurrency, 0),
      },
      {
        name: AssetClassNames.ALTERNATIVE_INV,
        total: this.tradeRecommendations
          .filter(
            (tr) =>
              tr.security.assetClass.name === AssetClassNames.ALTERNATIVE_INV
          )
          .reduce((total, tr) => total + tr.marketValueDisplayCurrency, 0),
      },
      {
        name: AssetClassNames.CASH,
        total:
          this.cashBalances.reduce(
            (total, c) => total + c.marketValueDisplayCurrency,
            0
          ) - netBuySell,
      },
    ];
    this.targetAssetClassesTotal = this.targetAssetClass.reduce(
      (prev, next) => prev + next.total,
      0
    );
  }

  private updatePercentages() {
    const targetModelTotal = this.tradeRecommendations
      .filter((tr) => tr.cModelPercentage !== 0)
      .reduce((total, tr) => tr.targetMarketValue + total, 0);

    for (let tradeRecommendation of this.tradeRecommendations) {
      if (tradeRecommendation.cModelPercentage !== 0) {
        tradeRecommendation.targetModelAllocation = +(
          (100 * tradeRecommendation.targetMarketValue) /
          targetModelTotal
        ).toFixed(2);
      }

      // Possible alternative to the switch
      const assetClass = tradeRecommendation.security.assetClass.name;
      if (assetClass === AssetClassNames.CASH) {
        continue;
      }

      tradeRecommendation.targetAssetClassAllocation = +(
        (100 * tradeRecommendation.targetMarketValue) /
        this.getAssetClassTotal(this.targetAssetClass, assetClass)
      ).toFixed(2);
    }

    this.updateAssetClasses();
  }

  public onAssetClassAllocationUpdate(
    tradeRecommendation: TradeRecommendation
  ) {
    // Only apply calculations if this is not due to some other change
    if (!this.modelChange && tradeRecommendation.targetAssetClassAllocation) {
      this.modelChange = true;

      const assetClass = tradeRecommendation.security.assetClass.name;
      if (assetClass !== AssetClassNames.CASH) {
        const targetMarketValue = +(
          (this.getAssetClassTotal(this.targetAssetClass, assetClass) *
            tradeRecommendation.targetAssetClassAllocation) /
          100
        ).toFixed(2);
        tradeRecommendation.targetMarketValue = targetMarketValue;
      }

      let priceEstimate = tradeRecommendation.price;
      var targetQuantity = +(
        tradeRecommendation.targetMarketValue / priceEstimate
      ).toFixed(0);
      tradeRecommendation.buySellQuantity =
        targetQuantity - tradeRecommendation.quantity;

      // this.updateAssetClasses();
      this.updatePercentages();

      this.modelChange = false;
    }
  }

  public onQuantityUpdate(tradeRecommendation: any) {
    // Only apply calculations if this is not due to some other change
    if (!this.modelChange) {
      this.modelChange = true;

      if (!tradeRecommendation.buySellQuantity) {
        tradeRecommendation.buySellQuantity = 0;
      }

      let priceEstimate = tradeRecommendation.price;
      let targetMarketValue =
        priceEstimate *
        (tradeRecommendation.quantity + tradeRecommendation.buySellQuantity);
      tradeRecommendation.targetMarketValue = +targetMarketValue.toFixed(2);

      // this.updateTargetCharts();
      this.updatePercentages();
      this.modelChange = false;
    }
  }

  public saveTrades() {
    this.isLoading = true;

    // this.tradeSet.trades = this.tradeRecommendations.map(tr => new Trade(tr))
    // this.tradingRepo.saveTrades(this.clientId, this.portfolioId, this.tradeSet)
    //   .then(() => this.onPortfolioSelect(this.activePortfolio))
    //   .catch(err => this.notifyjs(err.data))
    //   .finally(() => this.isLoading = false);
  }
}

export const portfolioSimulation: angular.IComponentOptions = {
  controller: PortfolioSimulationController,
  template: require("./portfolioSimulation.component.html"),
  bindings: {
    portfolio: "<",
    tradeRecommendations: "<",
    cashBalances: "<",
    tradeSheetOnly: "<",
  },
};



// WEBPACK FOOTER //
// ./src/app/simulations/components/portfolioSimulation.component.ts